/* Aelse, 2026-10-16: the Instant welcome popup, steadied from the theme.
   Her words: "it kinda popped up in phases", "its like half the height for a moment", "i dont like the janky half
   height", "its also fucked on mobile". Record: brands/aelse/shopify-theme/2026-10-14-safari-add-to-bag/_note.md
   Instant draws the popup inside its own shadow root, which the theme's CSS cannot reach, so this adds one stylesheet
   inside it:
   1. The seal has a width but no height, and its file only starts loading when the popup opens, so the card opened
      ~190 px short and then jumped. While a popup image loads it now holds a square, transparent, then fades in; the
      hold comes off once the file is in, so a non-square image put in later still shows at its own shape.
   2. Phones: the 100 px side padding on the email box and button (set for desktop; Instant has no phone setting)
      squeezed them to ~150 px. On phones they get 45 px a side, the same as her text blocks; the box and button are
      44 px tall (a thumb's tap size; the button was 28 px) and the box's type is 16 px, because iPhone Safari zooms
      the whole page when a field under 16 px is tapped.
   3. Short phone screens get a 144 px seal so the card fits without scrolling. Phone heading (her 22:11 ask,
      "ENTER / SOMEWHERE AELSE"): balanced, which breaks after ENTER because "SOMEWHERE AELSE" measures narrower than
      "ENTER SOMEWHERE" (286 vs 291 px at 22 px / 4 px tracking); where even that cannot fit on two lines, the tracking
      steps down 1 px at a time (to 2 px at most) until it does. Older iPhones without balance keep the plain wrap.
   4. The close button: Instant draws it as a white disc, which glared on her dark popup (21:47). It is now a thin
      outline in the popup's own text colour (read from the heading), so it suits a light or a dark popup.
   5. The card's 1 px border: Instant's default pale grey (#f3f4f6), invisible on cream, a white line on her dark
      popup ("its got an outline around it", 21:51). Transparent now; the card's own colour fills it.
   6. The page behind the popup is blurred (8 px) under Instant's dim ("i wish i could blur the background while the
      popup is up", 21:53). Decoration only, per her 2026-09-15 no-blur law: the card is a solid fill, so a phone that
      draws no blur simply shows the dim as before.
   7. Her 21:53 "i dont love the line breaks on mobile": the two text lines are balanced (no lone "piece." or "only.";
      older iPhones without text-wrap: balance keep the plain wrap). Her "couldnt ... change the text on the buttons":
      STEP INSIDE / CONTINUE take the site's own button type (Instrument Sans 12.5 px, weight 500, uppercase, .18em
      tracking, measured from "Enter the Wardrobe" on the live home page).
   8. The email box (her 22:15 pick "underline only" from now / A square / B underline): no box, a 1 px underline in
      the popup's text colour at 55% (full on focus), placeholder centred, on every screen.
   If Instant changes its markup the rules stop matching and the popup is drawn exactly as Instant draws it. */
(() => {
  if (window.aePopup) return;
  window.aePopup = { version: 1 };

  const WAIT = 'data-ae-wait';
  const CSS = `
    div:has(> form[role="dialog"]) { -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px); }
    form[role="dialog"] { border-color: transparent !important; }
    form[role="dialog"] p { text-wrap: balance; }
    form[role="dialog"] button[data-signup-form-action] { font-size: 12.5px !important; font-weight: 500 !important; letter-spacing: .18em !important; text-transform: uppercase !important; text-indent: .18em; }
    form[role="dialog"] input { background: transparent !important; border: 0 !important; border-radius: 0 !important; padding-left: 0 !important; padding-right: 0 !important; text-align: center !important; border-bottom: 1px solid var(--ae-ink, currentColor) !important; border-bottom-color: color-mix(in srgb, var(--ae-ink, currentColor) 55%, transparent) !important; }
    form[role="dialog"] input:focus { border-bottom-color: var(--ae-ink, currentColor) !important; }
    form[role="dialog"] > button[aria-label="Close"] { background: transparent !important; color: var(--ae-ink, inherit) !important; border-color: var(--ae-ink, currentColor) !important; }
    form[role="dialog"] img { transition: opacity .35s ease; }
    form[role="dialog"] img[${WAIT}] { aspect-ratio: 1 / 1; opacity: 0; }
    @media (max-width: 600px) {
      form[role="dialog"] label:has(input),
      form[role="dialog"] div:has(> button[data-signup-form-action]) { padding-left: 45px !important; padding-right: 45px !important; }
      form[role="dialog"] input { font-size: 16px !important; height: 44px !important; }
      form[role="dialog"] button[data-signup-form-action] { height: 44px !important; }
      form[role="dialog"] h1 { text-wrap: balance; }
      form[role="dialog"] h1[data-ae-ls] { letter-spacing: var(--ae-ls) !important; }
    }
    @media (max-width: 600px) and (max-height: 760px) {
      form[role="dialog"] img { width: 144px !important; }
    }
    @media (prefers-reduced-motion: reduce) {
      form[role="dialog"] img { transition: none; }
    }`;

  let sheet = null;
  try {
    sheet = new CSSStyleSheet();
    sheet.replaceSync(CSS);
  } catch (error) {
    sheet = null;
  }

  function addStyle(root) {
    if (sheet && 'adoptedStyleSheets' in root) {
      if (!root.adoptedStyleSheets.includes(sheet)) root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
    } else if (!root.querySelector('style[data-ae-popup]')) {
      const tag = document.createElement('style');
      tag.setAttribute('data-ae-popup', '');
      tag.textContent = CSS;
      root.appendChild(tag);
    }
  }

  // Hold the image's square until its file is in (or has failed), then let it fade in.
  function hold(img) {
    const src = img.getAttribute('src') || '';
    if (img.aeSrc === src) return;
    img.aeSrc = src;
    if (!src || img.complete) {
      img.removeAttribute(WAIT);
      return;
    }
    img.setAttribute(WAIT, '');
    const done = () => {
      if (img.aeSrc === src) img.removeAttribute(WAIT);
    };
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
    setTimeout(done, 5000);
  }

  // Phones: keep the heading on two lines, stepping its tracking down only when it would otherwise take three.
  const PHONE = window.matchMedia('(max-width: 600px)');
  const forms = new Set();

  function lineCount(el) {
    const range = document.createRange();
    range.selectNodeContents(el);
    return new Set(Array.from(range.getClientRects()).map((rect) => Math.round(rect.top))).size;
  }

  function fitHeading(form) {
    const heading = form.querySelector('h1');
    if (!heading) return;
    heading.removeAttribute('data-ae-ls');
    if (!PHONE.matches || lineCount(heading) <= 2) return;
    const start = parseFloat(getComputedStyle(heading).letterSpacing) || 0;
    for (let spacing = Math.floor(start) - 1; spacing >= 2; spacing -= 1) {
      heading.style.setProperty('--ae-ls', spacing + 'px');
      heading.setAttribute('data-ae-ls', '');
      if (lineCount(heading) <= 2) return;
    }
  }

  let refit = 0;
  addEventListener('resize', () => {
    cancelAnimationFrame(refit);
    refit = requestAnimationFrame(() => forms.forEach((form) => form.isConnected ? fitHeading(form) : forms.delete(form)));
  });

  // Mutation observers run before the next paint, so a new image is held before it is ever drawn at zero height.
  function watch(host) {
    const root = host.shadowRoot;
    if (!root) return false;
    if (root.aeWatched) return true;
    root.aeWatched = true;
    const scan = () => {
      addStyle(root);
      root.querySelectorAll('img').forEach(hold);
      // The close button follows the popup's own text colour, light popup or dark.
      const form = root.querySelector('form[role="dialog"]');
      const ink = form && form.querySelector('h1, p');
      if (ink) {
        const color = getComputedStyle(ink).color;
        if (form.style.getPropertyValue('--ae-ink') !== color) form.style.setProperty('--ae-ink', color);
      }
      if (form && !forms.has(form)) {
        forms.add(form);
        fitHeading(form);
        requestAnimationFrame(() => fitHeading(form));
        if (document.fonts) document.fonts.ready.then(() => fitHeading(form));
      }
    };
    new MutationObserver(scan).observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
    scan();
    return true;
  }

  const isHost = (node) => node.nodeType === 1 && /^ic-signup-form-/.test(node.id || '') && node.tagName !== 'STYLE';

  function tryHost(host, tries = 0) {
    if (watch(host) || tries > 60) return;
    setTimeout(() => tryHost(host, tries + 1), 50);
  }

  function start() {
    Array.from(document.body.children).filter(isHost).forEach((host) => tryHost(host));
    new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) if (isHost(node)) tryHost(node);
      }
    }).observe(document.body, { childList: true });
  }

  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });
})();
